'use client';

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaMapMarkerAlt, FaRoute, FaCamera, FaInfoCircle, FaCompass, FaFilter, FaArrowRight, FaTimes, FaClock, FaSun, FaLeaf, FaSnowflake, FaTree, FaMagic, FaCalendarAlt, FaLightbulb } from 'react-icons/fa';
import ElementSpotlight from './ElementSpotlight';

interface TutorialStep {
  title: string;
  description: string;
  icon: React.ReactNode;
  position: 'top' | 'right' | 'bottom' | 'left' | 'center';
  highlight?: string; // CSS selector to highlight
  animationClass?: string; // Optional CSS animation class
}

interface TutorialOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  lang: string;
}

const TutorialOverlay: React.FC<TutorialOverlayProps> = ({ isOpen, onClose, lang }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [showTutorial, setShowTutorial] = useState(isOpen);

  // Reset to first step when opening
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      setShowTutorial(true);
    }
  }, [isOpen]);

  // Tutorial steps with animations
  const tutorialSteps: TutorialStep[] = [
    {
      title: 'Welcome to Trail Discovery',
      description: 'Discover and explore hiking trails around Engelberg with our interactive map and navigation tools.',
      icon: <FaRoute className="text-blue-500 text-3xl" />,
      position: 'center',
      animationClass: 'pulse-animation'
    },
    {
      title: 'Perfect for Now',
      description: 'Use the "Perfect for Now" button to get smart trail recommendations based on current time, daylight, weather, and season.',
      icon: <FaMagic className="text-purple-500 text-3xl" />,
      position: 'top',
      highlight: '.smart-recommendations-button',
      animationClass: 'glow-animation'
    },
    {
      title: 'Daylight Aware',
      description: 'The system monitors remaining daylight and recommends trails you can safely complete before sunset, helping you plan safer hikes.',
      icon: <FaSun className="text-yellow-500 text-3xl" />,
      position: 'right',
      highlight: '.daylight-indicator',
      animationClass: 'pulse-animation'
    },
    {
      title: 'Seasonal Notes',
      description: 'Expand the seasonal notes section to see specific information about how trails change throughout the year. Current season is highlighted.',
      icon: <FaCalendarAlt className="text-blue-500 text-3xl" />,
      position: 'bottom',
      highlight: '.seasonal-notes',
      animationClass: 'bounce-animation'
    },
    {
      title: 'Explore the Map',
      description: 'View all available trails on the map. Click on any trail to see detailed information.',
      icon: <FaMapMarkerAlt className="text-green-500 text-3xl" />,
      position: 'center',
      highlight: '.trail-map-container',
      animationClass: 'bounce-animation'
    },
    {
      title: 'Filter Trails',
      description: 'Use the filters to find trails that match your preferences - by difficulty, distance, season, or surface type.',
      icon: <FaFilter className="text-purple-500 text-3xl" />,
      position: 'left',
      highlight: '.filter-sidebar',
      animationClass: 'slide-animation'
    },
    {
      title: 'Trail Details',
      description: 'View detailed information about each trail, including distance, duration, difficulty, and points of interest.',
      icon: <FaInfoCircle className="text-yellow-500 text-3xl" />,
      position: 'bottom',
      highlight: '.trail-details',
      animationClass: 'fade-animation'
    },
    {
      title: 'Start Navigation',
      description: 'When you find a trail you like, click "Start Navigation" to begin guided navigation along the trail.',
      icon: <FaCompass className="text-blue-500 text-3xl" />,
      position: 'bottom',
      highlight: '.start-navigation-button',
      animationClass: 'rotate-animation'
    },
    {
      title: 'Take Photos',
      description: 'During navigation, you can take photos at interesting points. These will be saved as branching points on your trail.',
      icon: <FaCamera className="text-red-500 text-3xl" />,
      position: 'right',
      highlight: '.photo-button',
      animationClass: 'flash-animation'
    }
  ];

  const handleNextStep = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleCloseTutorial();
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCloseTutorial = () => {
    setShowTutorial(false);
    setTimeout(() => {
      onClose();
    }, 500); // Wait for exit animation
  };

  // Get position classes based on step position
  const getPositionClasses = (position: string) => {
    switch (position) {
      case 'top':
        return 'top-10 left-1/2 transform -translate-x-1/2';
      case 'right':
        return 'top-1/2 right-10 transform -translate-y-1/2';
      case 'bottom':
        return 'bottom-10 left-1/2 transform -translate-x-1/2';
      case 'left':
        return 'top-1/2 left-10 transform -translate-y-1/2';
      case 'center':
      default:
        return 'top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2';
    }
  };

  if (!showTutorial) return null;

  return (
    <AnimatePresence mode="wait">
      {showTutorial && (
        <motion.div
          key="tutorial-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-gradient-to-b from-white/20 to-blue-50/30 backdrop-blur-[1px] flex items-center justify-center"
        >
          {/* Highlighted element overlay */}
          {/* Element Spotlight System */}
          {tutorialSteps[currentStep].highlight && (
            <ElementSpotlight
              selector={tutorialSteps[currentStep].highlight}
              title={tutorialSteps[currentStep].title}
            />
          )}

          {/* Tutorial step content */}
          <motion.div
            key={`step-${currentStep}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className={`absolute bg-white bg-opacity-75 backdrop-blur-[2px] rounded-lg shadow-xl ring-1 ring-white/50 p-6 max-w-md ${getPositionClasses(tutorialSteps[currentStep].position)}`}
            style={{ boxShadow: '0 0 15px rgba(255, 255, 255, 0.5), 0 0 30px rgba(255, 255, 255, 0.2)' }}
          >
            {/* Close button */}
            <button
              onClick={handleCloseTutorial}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 z-10"
              aria-label="Close tutorial"
            >
              <FaTimes />
            </button>

            {/* Step indicator */}
            <div className="flex justify-center mb-4">
              {tutorialSteps.map((_, index) => (
                <motion.div
                  key={index}
                  className={`w-2 h-2 rounded-full mx-1 ${index === currentStep ? 'bg-blue-500' : 'bg-gray-300'
                    }`}
                  animate={index === currentStep ? { scale: [1, 1.2, 1] } : {}}
                  transition={{ duration: 1, repeat: index === currentStep ? Infinity : 0, repeatType: "reverse" }}
                ></motion.div>
              ))}
            </div>

            {/* Icon with animation */}
            <div className="flex justify-center mb-4">
              <div className={`p-3 rounded-full bg-white bg-opacity-90 shadow-inner ${tutorialSteps[currentStep].animationClass || ''}`}>
                {tutorialSteps[currentStep].icon}
              </div>
            </div>

            {/* Content */}
            <h3 className="text-xl font-bold text-center mb-2">
              {tutorialSteps[currentStep].title}
            </h3>
            <p className="text-gray-700 text-center mb-6">
              {tutorialSteps[currentStep].description}
            </p>

            {/* Navigation buttons */}
            <div className="flex justify-between">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handlePrevStep}
                disabled={currentStep === 0}
                className={`px-4 py-2 rounded-md ${currentStep === 0
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 bg-opacity-80 text-gray-700 hover:bg-gray-300'
                  }`}
              >
                Back
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleNextStep}
                className="px-4 py-2 bg-blue-500 bg-opacity-90 text-white rounded-md hover:bg-blue-600 flex items-center"
              >
                {currentStep === tutorialSteps.length - 1 ? 'Finish' : 'Next'}
                <FaArrowRight className="ml-2" />
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Animation styles */}
      <style jsx global>{`
        .pulse-animation {
          animation: pulse 2s infinite;
        }
        
        .rotate-animation {
          animation: rotate 5s infinite linear;
        }
        
        .bounce-animation {
          animation: bounce 2s infinite;
        }
        
        .slide-animation {
          animation: slide 3s infinite;
        }
        
        .flash-animation {
          animation: flash 3s infinite;
        }
        
        .fade-animation {
          animation: fade 3s infinite;
        }
        
        .glow-animation {
          animation: glow 2s infinite;
        }
        
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); }
        }
        
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        
        @keyframes slide {
          0% { transform: translateX(0); }
          25% { transform: translateX(5px); }
          50% { transform: translateX(0); }
          75% { transform: translateX(-5px); }
          100% { transform: translateX(0); }
        }
        
        @keyframes flash {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        
        @keyframes fade {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        
        @keyframes glow {
          0% { box-shadow: 0 0 5px rgba(111, 66, 193, 0.5); }
          50% { box-shadow: 0 0 20px rgba(111, 66, 193, 0.8); }
          100% { box-shadow: 0 0 5px rgba(111, 66, 193, 0.5); }
        }
      `}</style>
    </AnimatePresence>
  );
};

export default TutorialOverlay;